import React from 'react';
import UniversalRouter from 'universal-router';

import { lazyWithSuspense, parseQueryString } from '@travel/utils';

import App from 'favoriteList/core/app';
import favoriteListResolver from 'favoriteList/pages/FavoriteList/resolver';

import paths from './paths';

import type { universalRouterProps } from './types';

const FavoriteList = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-FavoriteList" */ 'favoriteList/pages/FavoriteList'),
);
const ErrorPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ErrorPage" */ 'favoriteList/pages/ErrorPage'),
);

export default (props: universalRouterProps) => {
  const isServer = props.isServer || false;

  const routes = [
    {
      path: '/',
      async action(context: any) {
        let children;

        try {
          children = await context.next();
        } catch (e) {
          console.error(e);
          children = null;
        }

        return (
          <App {...context} key="app">
            {children}
          </App>
        );
      },
      children: [
        {
          path: paths.domesticProvider.path,
          chunkName: 'pages-FavoriteList',
          async action(context: any) {
            if (isServer) {
              await favoriteListResolver(props, context, true, false);
            } else if (!context.skipResolver) {
              favoriteListResolver(props, context, true, false);
            }

            return <FavoriteList {...context} isDomestic={true} isPlanList={false} />;
          },
        },
        {
          path: paths.overseasProvider.path,
          chunkName: 'pages-FavoriteList',
          async action(context: any) {
            if (isServer) {
              await favoriteListResolver(props, context, false, false);
            } else if (!context.skipResolver) {
              favoriteListResolver(props, context, false, false);
            }

            return <FavoriteList {...context} isDomestic={false} isPlanList={false} />;
          },
        },
        {
          path: paths.domesticPlan.path,
          chunkName: 'pages-FavoriteList',
          async action(context: any) {
            if (isServer) {
              await favoriteListResolver(props, context, true, true);
            } else if (!context.skipResolver) {
              favoriteListResolver(props, context, true, true);
            }

            return <FavoriteList {...context} isDomestic={true} isPlanList={true} />;
          },
        },
        {
          path: paths.overseasPlan.path,
          chunkName: 'pages-FavoriteList',
          async action(context: any) {
            if (isServer) {
              await favoriteListResolver(props, context, false, true);
            } else if (!context.skipResolver) {
              favoriteListResolver(props, context, false, true);
            }

            return <FavoriteList {...context} isDomestic={false} isPlanList={true} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          chunkName: 'pages-ErrorPage',
          async action(context: any) {
            // Please remove if you want to test the error page.
            if (process.env.NODE_ENV !== 'production') {
              props.res?.redirect('/hotel/domestic');
            }
            props.res?.status(400);
            return <ErrorPage />;
          },
        },
      ],
    },
  ];
  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (typeof context.route.action === 'function') {
        context.query = {};
        if (context.search) {
          // when redirect from omni with existing filter,
          // there will be ? added from the redirection
          const searchRegex = new RegExp('\\?$');
          const currentSearch = context.search;
          context.query = parseQueryString(
            searchRegex.test(currentSearch)
              ? currentSearch.substring(0, currentSearch.length - 1)
              : currentSearch,
          );
        }

        // Track initial chunks for SSR asset loading
        if (props.initialChunks && context.route.chunkName) {
          props.initialChunks.push(context.route.chunkName);
        }

        return context.route.action(context, params);
      }
      return undefined;
    },
  });
};
